import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ASSESSMENT_OUTCOMES } from "../services/adhd-assessment";
import { useUpdateAssessmentOutcome } from "../hooks/use-adhd-assessment";

// Care team records the clinician-relayed assessment verdict on an ADHD usage
// (surfaces on the patient's results page via dashboard-v2 resolve).
export function AssessmentOutcomeDialog({
  usageId,
  currentOutcome,
  currentNotes,
  trigger,
}: {
  usageId: string;
  currentOutcome?: string;
  currentNotes?: string;
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState(currentOutcome ?? "");
  const [notes, setNotes] = useState(currentNotes ?? "");
  const { mutate: saveOutcome, isPending } = useUpdateAssessmentOutcome();

  const handleSave = () => {
    saveOutcome(
      {
        usage_id: usageId,
        assessment_outcome: outcome,
        assessment_notes: notes.trim() || undefined,
      },
      { onSuccess: () => setOpen(false) }
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record exploration outcome</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Outcome</label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger>
                <SelectValue placeholder="Select the outcome" />
              </SelectTrigger>
              <SelectContent>
                {ASSESSMENT_OUTCOMES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Short context from the clinician (not shown to the patient)"
            />
          </div>
          <Button
            onClick={handleSave}
            disabled={!outcome || isPending}
            className="w-full"
          >
            {isPending ? "Saving..." : "Save outcome"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
